import { useState, useContext, useEffect } from "react";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css'; // or your preferred style
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Link as MuiLink,
    InputLabel,
    IconButton,
    InputAdornment,
    LinearProgress,
    OutlinedInput,
    Container
} from "@mui/material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";

import { auth } from "../config/FirebaseConfig";
import UserApi from "../services/UserApi";
import InvitationApi from "../services/InvitationApi";
import ProjectApi from "../services/ProjectApi";
import { UserContext } from "../context/UserContext";
import PageContainer from "../components/page-container/PageContainer";

export default function InviteSignUpPage() {

    const userApi = new UserApi();
    const invitationApi = new InvitationApi();
    const projectApi = new ProjectApi();
    const { refreshUser } = useContext(UserContext);
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [invite, setInvite] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [passwordStrength, setPasswordStrength] = useState("");
    const [passwordScore, setPasswordScore] = useState(0);
    const [passwordSuggestions, setPasswordSuggestions] = useState([]);

    const [userBody, setUserBody] = useState({ name: "", phone: "" });
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

    const navigate = useNavigate();

    useEffect(() => {
        async function fetchInvite() {
            const response = await invitationApi.getInvitationByToken(token);
            if (!response.success) {
                setSnackbar({ open: true, message: response.error, severity: "error" });
                setTimeout(() => {
                    navigate("/");
                }, 3000);
                return;
            }
            const data = response.data;
            if (!data || data.accepted) {
                setSnackbar({ open: true, message: "Invalid or already accepted invitation.", severity: "error" });
                setTimeout(() => {
                    navigate("/signin");
                }, 3000);
                return;
            }
            setEmail(data.email);
            setInvite(data);
            setLoading(false);
        }
        if (token) {
            fetchInvite();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const evaluatePassword = (pwd) => {
        const suggestions = [];
        let score = 0;

        if (pwd.length >= 8) score++;
        else suggestions.push("Use at least 8 characters");

        if (/[A-Z]/.test(pwd)) score++;
        else suggestions.push("Add at least one uppercase letter");

        if (/[a-z]/.test(pwd)) score++;
        else suggestions.push("Add at least one lowercase letter");

        if (/\d/.test(pwd)) score++;
        else suggestions.push("Add at least one number");

        if (/[\W_]/.test(pwd)) score++;
        else suggestions.push("Add at least one special character");

        setPasswordScore(score);
        setPasswordSuggestions(suggestions);

        if (score >= 5) return "Strong";
        if (score >= 3) return "Medium";
        return "Weak";
    };

    const handlePasswordChange = (e) => {
        const val = e.target.value;
        setPassword(val);
        const strength = evaluatePassword(val);
        setPasswordStrength(strength);
    };

    const isStrongPassword = (pwd) => evaluatePassword(pwd) === "Strong";

    const handleSignUp = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            setSnackbar({ open: true, message: "Passwords do not match.", severity: "error" });
            return;
        }

        if (!isStrongPassword(password)) {
            setSnackbar({
                open: true,
                message: "Password must meet all strength criteria.",
                severity: "error",
            });
            return;
        }

        setLoading(true);

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            await updateProfile(userCredential.user, { displayName: userBody?.name });

            await userApi.createUser(userCredential.user.uid, {
                name: userBody.name,
                email: userCredential.user.email,
                phone: userBody.phone,
                role: invite.role,
                companyId: invite.companyId || null, // Optional company ID
            });

            const userResponse = await userApi.getUser(userCredential.user.uid);
            
            await invitationApi.hardDeleteInvitation(invite.id);

            await projectApi.createProjectParticipant(invite.projectId, invite.invitedById, invite.role, userCredential.user.uid, invite.invitedBy, invite.companyId, invite.company, userResponse.user);
            await projectApi.addProjectParticipantIds(invite.projectId, userCredential.user.uid);
            refreshUser();
            navigate("/project");
        } catch (err) {
            setSnackbar({ open: true, message: err.message, severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const getStrengthColor = () => {
        switch (passwordStrength) {
            case "Strong":
                return "success";
            case "Medium":
                return "warning";
            case "Weak":
                return "error";
            default:
                return "inherit";
        }
    };

    return (
        <PageContainer>
            {!loading && <Box
                component="form"
                onSubmit={handleSignUp}
                sx={{ mt: 8, p: 4, boxShadow: 3, borderRadius: 2 }}
            >
                <Typography variant="h3" align="center" gutterBottom>
                    Invite Sign Up
                </Typography>

                <TextField
                    margin="normal"
                    fullWidth
                    label="Full Name"
                    value={userBody.name}
                    onChange={(e) => setUserBody({ ...userBody, name: e.target.value })}
                    required
                />

                <TextField
                    margin="normal"
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled
                />

                {/* Password */}
                <TextField
                    margin="normal"
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Strength Bar - only show if password is not empty */}
                {password.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Strength: {passwordStrength || "N/A"}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={(passwordScore / 5) * 100}
                            color={getStrengthColor()}
                            sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
                        />
                        {passwordSuggestions.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                {passwordSuggestions.map((suggestion, i) => (
                                    <Typography key={i} variant="caption" color="text.secondary" display="block">
                                        • {suggestion}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Confirm Password */}
                <TextField
                    margin="normal"
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    error={confirmPassword && confirmPassword !== password}
                    helperText={
                        confirmPassword && confirmPassword !== password ? "Passwords do not match" : ""
                    }
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    edge="end"
                                >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{ mt: 2 }}>
                    <InputLabel htmlFor="phone-number" sx={{ mb: 1, ml: '12px' }}>
                        Phone Number
                    </InputLabel>
                    <PhoneInput
                        country={'za'}
                        value={userBody.phone}
                        onChange={(phone) => setUserBody({ ...userBody, phone: phone })}
                        inputComponent={OutlinedInput}
                        inputStyle={{ width: '100%', borderRadius: 16, backgroundColor: 'transparent', color: '#ffffff' }}
                        specialLabel=""
                        required
                        inputProps={{
                            id: 'phone-number',
                            variant: 'outlined',
                            'aria-label': 'Phone Number',
                        }}

                    />
                </Box>

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{ mt: 3, mb: 1 }}
                >
                    {loading ? <CircularProgress size={24} /> : "Create Account"}
                </Button>

                <MuiLink
                    component={RouterLink}
                    to="/signin"
                    variant="body2"
                    display="block"
                    align="center"
                    sx={{ mt: 2 }}
                >
                    Already have an account? Sign In
                </MuiLink>
            </Box>}

            {loading && <Container maxWidth="sm" sx={{ textAlign: 'center', mt: 8 }}>
                <CircularProgress />
            </Container>}

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </PageContainer>
    );
}
